import axios from 'axios'
import { defineStore } from 'pinia'
import { BASE_URL } from '@/environment/environment'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'

import type { IAuditArchiveFilters } from '@/modules/audits/stores/useAuditArchiveStore/types/IAuditArchiveFilters'
import type { IAuditArchiveItem } from '@/modules/audits/stores/useAuditArchiveStore/types/IAuditArchiveItem'
import type { IAuditArchiveStoreState } from '@/modules/audits/stores/useAuditArchiveStore/types/IAuditArchiveStoreState'

export const useAuditArchiveStore = defineStore('auditArchive', {
  state: (): IAuditArchiveStoreState => ({
    audits: [],
    nextCursor: null,
    totalAuditsCount: 0,
    itemsPerPage: 25,
    page: 1,
    currentSortString: null,

    // Инициализация строго по интерфейсу IAuditArchiveFilters
    filters: {
      numbers: [],
      normatives: [],
      requirements: [],
      departments: [],
      date_start: null,
      date_end: null,
      types: [],
      status: [],
      search: '',
    },

    filterChoices: {},
    searchChoices: {},
    filterCursors: {},
    filterLoading: {},
    filterStrategies: {},
  }),

  actions: {
    async fetchFilterChoices(fieldKey: string, search = '', cursor?: string) {
      if (!fieldKey) return
      this.filterLoading[fieldKey] = true

      try {
        const params: any = { field: fieldKey }

        if (search) params.search_field = search
        if (cursor) params.cursor = cursor

        const filters = this.filters as IAuditArchiveFilters

        if (fieldKey !== 'numbers' && filters.numbers.length) {
          params.numbers = filters.numbers.join(',')
        }

        if (fieldKey !== 'normatives' && filters.normatives.length) {
          params.normatives = filters.normatives.join(',')
        }

        if (fieldKey !== 'requirements' && filters.requirements.length) {
          params.requirements = filters.requirements.join(',')
        }

        if (fieldKey !== 'departments' && filters.departments.length) {
          params.departments = filters.departments.join(',')
        }

        if (fieldKey !== 'types' && filters.types.length) {
          params.types = filters.types.join(',')
        }

        if (fieldKey !== 'status' && filters.status.length) {
          params.status = filters.status.join(',')
        }

        if (fieldKey !== 'date_start' && filters.date_start) {
          params.date_start = filters.date_start
        }

        if (fieldKey !== 'date_end' && filters.date_end) {
          params.date_end = filters.date_end
        }

        const response = await axios.get(`${BASE_URL}/api/audit/archive/filter-choices/`, { params })
        const { results, next, strategy } = response.data

        this.filterStrategies[fieldKey] = strategy || 'independent'

        if (search) {
          this.searchChoices[fieldKey] = cursor
            ? [...(this.searchChoices[fieldKey] || []), ...results]
            : results
        } else {
          this.filterChoices[fieldKey] = cursor
            ? [...(this.filterChoices[fieldKey] || []), ...results]
            : results
        }

        this.filterCursors[fieldKey] = next
      } catch (e) {
        console.error(`Ошибка загрузки фильтров архива [${fieldKey}]:`, e)
      } finally {
        this.filterLoading[fieldKey] = false
      }
    },

    async fetchAudits(cursor?: string, itemsPerPage?: number): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const params: Record<string, any> = {}

        if (this.currentSortString) {
          params.sort = this.currentSortString
        }

        if (itemsPerPage) {
          params.page_size = itemsPerPage
          this.itemsPerPage = itemsPerPage
        }

        if (cursor) params.cursor = cursor

        const filters = this.filters as IAuditArchiveFilters

        if (filters.numbers.length) {
          params.numbers = filters.numbers.join(',')
        }

        if (filters.normatives.length) {
          params.normatives = filters.normatives.join(',')
        }

        if (filters.requirements.length) {
          params.requirements = filters.requirements.join(',')
        }

        if (filters.departments.length) {
          params.departments = filters.departments.join(',')
        }

        if (filters.types.length) {
          params.types = filters.types.join(',')
        }

        if (filters.status.length) {
          params.status = filters.status.join(',')
        }

        if (filters.search) {
          params.search = filters.search
        }

        if (filters.date_start) {
          params.date_start = filters.date_start
        }

        if (filters.date_end) {
          params.date_end = filters.date_end
        }

        const res = await axios.get(`${BASE_URL}/api/audit/archive/`, { params })
        const results: IAuditArchiveItem[] = res.data.results ?? res.data

        this.nextCursor = res.data.next

        if (cursor) {
          this.audits = [...this.audits, ...results]
        } else {
          this.audits = results
        }

        this.totalAuditsCount = res.data.count
      } catch (error) {
        const message = getApiError(error, 'Не удалось загрузить архив аудитов')
        console.error(`❌ Ошибка загрузки архива аудитов: ${String(error)}`)
        feedbackStore.showToast({ type: 'error', message })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    resetFilters() {
      this.filters = {
        numbers: [],
        normatives: [],
        requirements: [],
        departments: [],
        date_start: null,
        date_end: null,
        types: [],
        status: [],
        search: '',
      }
      this.currentSortString = null
      this.page = 1
    },
  },
})
